import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTheme } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import Sidebar from '../components/Sidebar';
import { 
  ArrowLeft, 
  CheckSquare, 
  Sun, 
  Moon, 
  Sparkles,
  Clock,
  User,
  ChevronRight,
  CheckCircle,
  Archive
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompletedTasks = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCompletedTasks = useCallback(async () => {
    try {
      const response = await fetch(`${API}/tasks/completed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('Error fetching completed tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCompletedTasks();
  }, [fetchCompletedTasks]);

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'priority-high',
      medium: 'priority-medium',
      low: 'priority-low'
    };
    return styles[priority] || '';
  };

  const getRoleLabel = (task) => {
    if (task.assigned_to === user?.id) {
      return { label: 'Primary', className: 'bg-blue-500/20 text-blue-600 dark:text-blue-400' };
    }
    if (task.assigned_by === user?.id) {
      return { label: 'Owner', className: 'bg-green-500/20 text-green-600 dark:text-green-400' };
    }
    if (task.secondary_participants?.includes(user?.id)) {
      return { label: 'Secondary', className: 'bg-amber-500/20 text-amber-600 dark:text-amber-400' };
    }
    return null;
  };

  // Group tasks by completion date (month/year)
  const groupedTasks = tasks.reduce((acc, task) => {
    const date = parseISO(task.created_at);
    const key = format(date, 'MMMM yyyy');
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(task);
    return acc;
  }, {});

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-muted-foreground">Loading completed tasks...</div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-lg border-b">
          <div className="px-4 sm:px-6 py-3 sm:py-4 pl-14 lg:pl-6">
            <h1 className="font-heading text-xl sm:text-2xl font-bold">Completed Tasks</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">{tasks.length} completed tasks</p>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 sm:p-6">
        {/* Stats */}
        <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card className="flex-1">
            <CardContent className="p-4 sm:p-6 flex items-center gap-3 sm:gap-4">
              <div className="p-2 sm:p-3 rounded-xl bg-emerald-500/10">
                <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-500" />
              </div>
              <div>
                <p className="text-3xl font-bold">{tasks.length}</p>
                <p className="text-muted-foreground">Total Completed Tasks</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Task List */}
        {tasks.length === 0 ? (
          <Card>
            <CardContent className="py-16">
              <div className="flex flex-col items-center justify-center text-muted-foreground">
                <CheckCircle className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-lg font-medium">No completed tasks yet</p>
                <p className="text-sm">Tasks you complete will appear here</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedTasks).map(([month, monthTasks]) => (
              <Card key={month}>
                <CardHeader>
                  <CardTitle className="font-heading text-lg flex items-center gap-2">
                    <Archive className="w-5 h-5" />
                    {month}
                    <Badge variant="outline" className="ml-2">{monthTasks.length} tasks</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {monthTasks.map((task) => {
                      const roleInfo = getRoleLabel(task);
                      return (
                        <div
                          key={task.id}
                          className="task-card p-4 rounded-xl border bg-card cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => navigate(`/tasks/${task.id}`)}
                          data-testid={`completed-task-${task.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                <h3 className="font-medium">{task.name}</h3>
                                {task.ai_priority && (
                                  <Badge variant="outline" className={`${getPriorityBadge(task.ai_priority)} text-xs`}>
                                    <Sparkles className="w-3 h-3 mr-1" />
                                    {task.ai_priority}
                                  </Badge>
                                )}
                                {roleInfo && (
                                  <Badge variant="outline" className={`${roleInfo.className} text-xs`}>
                                    {roleInfo.label}
                                  </Badge>
                                )}
                              </div>
                              {task.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{task.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Due: {format(parseISO(task.due_date), 'PP')}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {task.assigned_to === user?.id ? (
                                    <>From: {task.assigned_by_name}</>
                                  ) : (
                                    <>To: {task.assigned_to_name}</>
                                  )}
                                </span>
                                <span>
                                  Completed: {formatDistanceToNow(parseISO(task.created_at), { addSuffix: true })}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
      </div>
    </Sidebar>
  );
};

export default CompletedTasks;
